import {
  CreateMedicineItemPayload,
  MedicineItemType,
  MedicineStatus,
  MedicineTimingRuleType,
  PendingChangeType,
  PillboxItem,
  PillboxPendingChange,
} from "@/types/pillbox";

export interface PendingGroup {
  key: string;
  itemType: MedicineItemType;
  name: string;
  items: PillboxPendingChange[];
}

// Groups ADD pending changes by case-insensitive (itemType, name) — the
// same medicine mentioned across multiple DIFFERENT prescriptions collapses
// into one review card ("In N prescriptions") instead of one per
// prescription. Same-name entries that all come from the SAME prescription
// (e.g. a thyroid dose split across two lines, "37.5mcg" and "50mcg" for
// alternate days) are NOT the same case — that's the one prescription
// listing two genuinely distinct doses, and each must stay its own
// reviewable card, not collapse into a dose-less "In 1 prescription" blob.
// DOSE_CHANGE/DISCONTINUE changes aren't part of this review flow yet.
export function groupPendingChanges(
  pending: PillboxPendingChange[]
): PendingGroup[] {
  const byName = new Map<string, PendingGroup>();
  for (const item of pending) {
    if (item.changeType !== PendingChangeType.ADD) continue;
    const fields = item.proposedFields as Partial<CreateMedicineItemPayload>;
    const name = (fields.name || "").trim();
    const key = `${item.itemType}::${name.toLowerCase()}`;
    if (!byName.has(key)) {
      byName.set(key, { key, itemType: item.itemType, name, items: [] });
    }
    byName.get(key)!.items.push(item);
  }

  const result: PendingGroup[] = [];
  for (const group of byName.values()) {
    const distinctPrescriptions = new Set(
      group.items.map((item) => item.sourcePrescriptionId)
    );
    if (distinctPrescriptions.size > 1) {
      // Genuinely proposed by more than one prescription — keep merged.
      result.push(group);
    } else {
      // Same single prescription listed this name more than once (or just
      // once) — every item stays its own individually-reviewable group.
      for (const item of group.items) {
        result.push({
          key: `${group.key}::${item.id}`,
          itemType: group.itemType,
          name: group.name,
          items: [item],
        });
      }
    }
  }
  return sortGroupsChronologically(result);
}

// Most recent prescription first — the screen is framed as "recent
// prescriptions", so the newest visit is what a reviewer expects at the top.
// A group spanning several prescriptions sorts by its most recent one.
// dateOfVisit is a YYYY-MM-DD string, so lexicographic compare is already
// chronological and sidesteps timezone shifts from parsing to Date.
// Undated groups sink to the bottom rather than floating above real dates,
// and name/key break ties so the order stays stable across re-renders.
function sortGroupsChronologically(groups: PendingGroup[]): PendingGroup[] {
  const latestVisit = (group: PendingGroup): string =>
    group.items.reduce(
      (latest, item) =>
        item.dateOfVisit && item.dateOfVisit > latest
          ? item.dateOfVisit
          : latest,
      ""
    );

  return [...groups].sort((a, b) => {
    const dateA = latestVisit(a);
    const dateB = latestVisit(b);
    if (dateA !== dateB) {
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateB.localeCompare(dateA);
    }
    return a.name.localeCompare(b.name) || a.key.localeCompare(b.key);
  });
}

// Synthesizes a PillboxItem-shaped draft from a pending change's
// proposedFields, so the same AddMedicine.tsx prefill logic used for
// edit/restore modes can be reused for the review-medication screen.
// Start date defaults to the prescription's date, not today — R2/Flow1/3.
export function mapPendingChangeToDraftItem(
  pending: PillboxPendingChange
): PillboxItem {
  const fields = pending.proposedFields as Partial<CreateMedicineItemPayload>;
  const timingRuleType =
    fields.timingRuleType ??
    (fields.slots?.length || fields.dayFrequency
      ? MedicineTimingRuleType.FIXED_SLOTS
      : MedicineTimingRuleType.CONDITIONAL);

  return {
    id: pending.id,
    itemType: pending.itemType,
    memberId: pending.memberId,
    name: fields.name || "",
    dose: fields.dose,
    frequency: fields.frequency,
    dayFrequency: fields.dayFrequency,
    timingRuleType,
    slots: fields.slots,
    recurrenceIntervalDays: fields.recurrenceIntervalDays,
    selectedWeekdays: fields.selectedWeekdays,
    route: fields.route,
    notes: fields.notes,
    reasonForTaking: fields.reasonForTaking,
    foodContext: fields.foodContext,
    doctorName: pending.doctorName,
    dateOfVisit: pending.dateOfVisit,
    prescriptionName: pending.prescriptionName,
    documentId: pending.sourceDocumentId,
    startDate: fields.startDate || pending.dateOfVisit || "",
    endDate: fields.endDate,
    status: MedicineStatus.ACTIVE,
    inShoppingList: false,
    hasOverlap: false,
  };
}
